// Single entry point for "how long is this video" — works for a direct MP4/webm
// URL (native <video> metadata) and for an HLS (.m3u8) URL (master or media
// playlist alike), so callers don't need to know which kind of URL they have.
//
// HLS specifics handled here:
//  - Master playlists (#EXT-X-STREAM-INF present) have no #EXTINF entries of
//    their own — we follow the first variant stream and measure that instead.
//  - Variant/segment URIs in a playlist are frequently relative, so every URL
//    we follow is resolved against the playlist that referenced it, not just
//    the original URL the caller passed in.
//  - A playlist without #EXT-X-ENDLIST is still being appended to (recording
//    in progress) — the summed duration is real but partial, so isComplete
//    is surfaced to the caller instead of silently returning a "final" number.

export const isHlsUrl = (url) => typeof url === 'string' && /\.m3u8(\?.*)?(#.*)?$/i.test(url);

const resolveUrl = (maybeRelativeUrl, baseUrl) => {
  try {
    return new URL(maybeRelativeUrl, baseUrl).href;
  } catch (e) {
    // Not a valid URL to resolve (shouldn't happen for well-formed playlists) -
    // fall back to whatever the playlist literally said.
    return maybeRelativeUrl;
  }
};

const fetchPlaylistText = async (url) => {
  const response = await fetch(url, { cache: 'no-store' });
  if (!response.ok) {
    throw new Error(`Failed to fetch m3u8 (${response.status}): ${url}`);
  }
  return response.text();
};

// Parses one already-fetched playlist body into either:
//   { type: 'master', variantUrl }                        - follow this next
//   { type: 'media', durationSec, isComplete, segments }   - final answer
// `segments` carries each segment's absolute URL alongside its own #EXTINF
// length, so a caller that needs more than the total (measuring the recording's
// byte size, say - see videoSize.js) doesn't have to re-parse the playlist.
const parsePlaylist = (text, playlistUrl) => {
  const lines = text.split(/\r?\n/).map((line) => line.trim());
  const isMaster = lines.some((line) => line.startsWith('#EXT-X-STREAM-INF'));

  if (isMaster) {
    // The URI for a variant stream is the first non-comment, non-empty line
    // that follows its #EXT-X-STREAM-INF tag.
    for (let i = 0; i < lines.length; i++) {
      if (!lines[i].startsWith('#EXT-X-STREAM-INF')) continue;
      for (let j = i + 1; j < lines.length; j++) {
        if (lines[j] && !lines[j].startsWith('#')) {
          return { type: 'master', variantUrl: resolveUrl(lines[j], playlistUrl) };
        }
      }
    }
    // Malformed master playlist - STREAM-INF tag(s) but no URI followed any of them.
    return { type: 'media', durationSec: 0, isComplete: false, segments: [] };
  }

  let durationSec = 0;
  let isComplete = false;
  const segments = [];
  // An #EXTINF tag states the length of the segment whose URI follows it, so
  // hold onto it until that URI line actually shows up.
  let pendingSegmentDuration = 0;
  for (const line of lines) {
    if (line.startsWith('#EXTINF:')) {
      const match = line.match(/^#EXTINF:([\d.]+)/);
      if (match) {
        pendingSegmentDuration = parseFloat(match[1]);
        durationSec += pendingSegmentDuration;
      }
    } else if (line.startsWith('#EXT-X-ENDLIST')) {
      isComplete = true;
    } else if (line && !line.startsWith('#')) {
      segments.push({ url: resolveUrl(line, playlistUrl), durationSec: pendingSegmentDuration });
      pendingSegmentDuration = 0;
    }
  }
  return { type: 'media', durationSec, isComplete, segments };
};

// Follows an HLS URL (master or media playlist alike) down to the media playlist
// that actually lists segments, and hands back everything parsed off it.
// Master -> media is the only hop our encoder ever produces, but follow
// defensively (capped) in case a master ever points at another master.
export const resolveHlsMediaPlaylist = async (url, depth = 0) => {
  if (depth > 5) {
    throw new Error('Too many redirects while resolving m3u8 variant playlist');
  }
  const text = await fetchPlaylistText(url);
  const parsed = parsePlaylist(text, url);
  if (parsed.type === 'master') {
    return resolveHlsMediaPlaylist(parsed.variantUrl, depth + 1);
  }
  return {
    playlistUrl: url,
    durationSec: parsed.durationSec,
    isComplete: parsed.isComplete,
    segments: parsed.segments,
  };
};

// Existing MP4 approach (reads real duration off the browser's own media
// metadata) - unchanged, just wrapped in a promise so it shares an interface
// with the HLS path above.
const getMp4Duration = (url) =>
  new Promise((resolve, reject) => {
    const video = document.createElement('video');
    video.preload = 'metadata';
    video.muted = true;
    const cleanup = () => {
      video.onloadedmetadata = null;
      video.onerror = null;
      video.src = '';
    };
    video.onloadedmetadata = () => {
      const durationSec = video.duration;
      cleanup();
      if (durationSec && isFinite(durationSec)) {
        resolve({ durationSec, isComplete: true });
      } else {
        reject(new Error('Could not read a finite duration from video metadata'));
      }
    };
    video.onerror = () => {
      cleanup();
      reject(new Error(`Failed to load video metadata: ${url}`));
    };
    video.src = url;
  });

const toMinSec = (durationSec) => {
  const totalSeconds = Math.max(0, Math.round(durationSec || 0));
  return { minutes: Math.floor(totalSeconds / 60), seconds: totalSeconds % 60 };
};

/**
 * Resolves to { durationSec, minutes, seconds, isComplete, source }.
 * - durationSec/minutes/seconds: total duration (minutes/seconds split for display).
 * - isComplete: false only for an HLS media playlist missing #EXT-X-ENDLIST,
 *   i.e. the recording is still live/in-progress and the number is partial.
 *   Always true for MP4 (a finished file's duration is always final).
 * - source: 'hls' | 'mp4', in case a caller wants to know which path was taken.
 */
export const getVideoDuration = async (url) => {
  if (!url) throw new Error('getVideoDuration: url is required');
  if (isHlsUrl(url)) {
    const { durationSec, isComplete } = await resolveHlsMediaPlaylist(url);
    return { durationSec, ...toMinSec(durationSec), isComplete, source: 'hls' };
  }
  const { durationSec, isComplete } = await getMp4Duration(url);
  return { durationSec, ...toMinSec(durationSec), isComplete, source: 'mp4' };
};

export default getVideoDuration;
